import { Button, Grid, Paper, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@material-ui/core';
import { unstable_Box as Box } from '@material-ui/core/Box';
import { Close as CloseIcon, Done as DoneIcon } from '@material-ui/icons';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircle';
import gql from 'graphql-tag';
import moment from 'moment';
import * as qs from 'query-string';
import React from 'react';

import { Account, App, CreditCard, Membership, Payline, helpers } from '@aps-management/primapp-common';

import { Screen } from '_components/core';
import { Alert, CreditCardPreview, DialogPaymentIP, PaylineWidgetFrame } from '_components/elements';
import apolloClient from '_utils/apolloClient';
import i18n from '_utils/i18n';
import masterClient from '_utils/masterClient';
import store from '_config/store';

import { PAYLINE_FLOW_TYPES, isExpectedPaylineOrigin } from '../../services/payline';

// Échéancier lu directement dans le master (source stable), utilisé en repli
// quand le WS live du club ne renvoie plus la cotisation (saison clôturée).
const CHECK_MEMBERSHIP_SCHEDULE_FULL = gql`
  query CheckMembershipSchedule($golfId: String!, $accountId: String!, $reference: String!) {
    checkMembershipSchedule(golfId: $golfId, accountId: $accountId, reference: $reference) {
      reference
      year
      amount
      frequency
      createdAt
      total {
        amount
        paid
        due
      }
      paymentSchedule {
        date
        amount
        status
      }
    }
  }
`;

/* */
class ConfirmMembershipSchedule extends React.Component {
  /* */
  constructor(props) {
    super(props);

    this.authorizationAmount = 100;

    this.state = {
      // ux
      error: null,
      screenLoading: true,
      buttonLoading: false,
      // data
      golf: null,
      account: null,
      membership: null,
      currentCard: null, // carte existante (expirée ou non)
      selectedCard: null, // carte sélectionnée pour l'échéancier
      isFullyCompleted: false, // membershipSchedule existant - prêt pour prélèvement
      paylineToken: null,
      isIframeReturn: false,
      status: null,
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
  }

  /* */
  componentDidMount() {
    // Handle Payline iframe return (redirect fallback)
    if (window !== window.parent) {
      if (window.location.search.includes('payline_action=')) {
        const urlParams = new URLSearchParams(window.location.search);
        const action = urlParams.get('payline_action');
        const token = urlParams.get('token') || urlParams.get('paylinetoken');

        window.parent.postMessage(
          JSON.stringify({
            type: action === 'cancel' ? 'PAYLINE_CANCEL' : 'PAYLINE_SUCCESS',
            data: { token },
          }),
          '*',
        );
        this.setState({ isIframeReturn: true });
        return;
      }
    }
    this.load();
    window.addEventListener('message', this.handlePostMessage);
  }

  componentWillUnmount() {
    window.removeEventListener('message', this.handlePostMessage);
  }

  handlePostMessage = (event) => {
    try {
      // Contrôle d'origine : sans lui, n'importe quel document capable de poster un
      // message à cette fenêtre déclenchait le traitement de succès Payline.
      if (!isExpectedPaylineOrigin(event.origin)) return;

      const msg = JSON.parse(event.data);

      if (msg.type === 'PAYLINE_SUCCESS') {
        this.handlePaylineCardSuccess(msg.data);
      } else if (msg.type === 'PAYLINE_ERROR') {
        this.handlePaylineCardError(msg.data, 'Le paiement a échoué. Veuillez réessayer.');
      } else if (msg.type === 'PAYLINE_CANCEL') {
        this.handlePaylineCardError(msg.data, 'Le paiement a été annulé.');
      }
    } catch (err) {
      // Silently ignore non-JSON messages
    }
  };

  /* */
  load = () => {
    const { reference } = this.props.match.params;
    const { token } = qs.parse(this.props.location.search);

    if (token && reference) {
      Account.api
        .getAccountByToken(apolloClient, { token })
        .then((account) => {
          if (account) {
            const matching = account.matches.find((m) => (m.token === token ? m : false));
            if (!matching) {
              throw new Error('Client non trouvé');
            }

            return App.api.getGolfInfo(apolloClient, { id: matching.golfId }).then(({ golf }) => {
              document.title = (golf && golf.name) || 'prima.golf';

              // set locale
              i18n.locale = golf.locale;
              this.currency = golf.currency;

              return Promise.all([
                // Le WS du club peut être indisponible ou en erreur : ne pas
                // faire échouer toute la page, le repli master peut suffire.
                Membership.api
                  .getExercises(apolloClient, {
                    golfId: golf.id,
                    accountId: account.id,
                  })
                  .catch(() => null),
                masterClient
                  .query({
                    query: CHECK_MEMBERSHIP_SCHEDULE_FULL,
                    variables: {
                      reference,
                      golfId: golf.id,
                      accountId: account.id,
                    },
                    fetchPolicy: 'no-cache',
                  })
                  .then((res) => res.data.checkMembershipSchedule),
              ]).then((data) => {
                const scheduleFromMaster = data[1];

                let membership = null;

                // 1) Source primaire : le WS live du club (exercice courant).
                if (data[0] && data[0].exercises && data[0].exercises.exercises) {
                  data[0].exercises.exercises.forEach((e) => {
                    (e.memberships || []).forEach((m) => {
                      if (m.reference === reference && m.paymentSchedule.length > 0) {
                        membership = m;
                      }
                    });
                  });
                }

                // 2) Repli : le WS ne renvoie plus cette cotisation (saison
                //    clôturée), mais l'échéancier existe toujours dans le master.
                if (
                  !membership &&
                  scheduleFromMaster &&
                  scheduleFromMaster.paymentSchedule &&
                  scheduleFromMaster.paymentSchedule.length > 0
                ) {
                  membership = {
                    reference: scheduleFromMaster.reference,
                    year: scheduleFromMaster.year,
                    members: [{ category: scheduleFromMaster.year }],
                    total: scheduleFromMaster.total,
                    paymentSchedule: scheduleFromMaster.paymentSchedule,
                  };
                }

                if (!membership) {
                  throw new Error('Cotisation non trouvée');
                }

                // « L'échéancier existe déjà » ne vaut que pour LA cotisation
                // affichée : la référence est réutilisée d'une saison à l'autre,
                // et un échéancier de la saison passée (ou un ancien paiement
                // comptant, frequency 'single') ne doit pas faire croire que
                // celui de la nouvelle saison est en place. Sans ce périmètre,
                // l'écran affichait « Carte renseignée avec succès » et
                // n'appelait jamais setMembershipSchedule : aucun prélèvement
                // n'était planifié pour la nouvelle saison.
                let isFullyCompleted = false;
                if (
                  scheduleFromMaster &&
                  scheduleFromMaster.reference &&
                  scheduleFromMaster.frequency !== 'single' &&
                  String(scheduleFromMaster.year) === String(membership.year)
                ) {
                  isFullyCompleted = true;
                }

                return CreditCard.api
                  .getCreditCards(apolloClient, { accountId: account.id })
                  .then(({ creditCards }) => {
                    let myCard = null;
                    if (creditCards[0]) {
                      myCard = {
                        ...creditCards[0],
                        ...CreditCard.functions.getStatus(creditCards[0].dueDate),
                      };
                    }

                    this.setState({
                      golf,
                      account,
                      membership,
                      isFullyCompleted,
                      currentCard: myCard,
                      selectedCard: myCard,
                    });

                    // Dispatch golf to Redux store so x-golf header is sent for Payline API calls
                    store.dispatch({
                      type: 'app/LOAD_GOLF',
                      payload: { ...golf, options: {} },
                    });
                  });
              });
            });
          }
          throw new Error('Client non trouvé');
        })
        .catch((error) => this.setState({ error }))
        .finally(() => this.setState({ screenLoading: false }));
    }
  };

  /* */
  renderSchedule() {
    const { membership } = this.state;

    const { paymentSchedule: schedule } = membership;

    const total = {
      paid: membership.total.paid,
      amount: membership.total.amount,
      due: membership.total.amount - membership.total.paid,
    };

    return (
      <Box>
        <Paper>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{'Date'}</TableCell>
                <TableCell align='right'>{'Montant'}</TableCell>
                <TableCell align='right'>{'Réglé'}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {schedule.map((l, i) => (
                <TableRow key={i}>
                  <TableCell>{i18n.l('date.formats.default', moment(l.date).toDate())}</TableCell>
                  <TableCell align='right'>{i18n.l('currency', l.amount / 100)}</TableCell>
                  <TableCell align='right'>
                    {l.status === 1 && <DoneIcon color='primary' />}
                    {l.status === 2 && <CloseIcon color='error' />}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
        <br />
        <Typography gutterBottom color='textSecondary'>
          {`Montant déjà réglé : ${i18n.l('currency', total.paid / 100)}`}
        </Typography>
        <Typography gutterBottom color='textSecondary'>
          {`Montant restant dû : ${i18n.l('currency', total.due / 100)}`}
        </Typography>
      </Box>
    );
  }

  /* */
  renderForm() {
    const { buttonLoading, currentCard, isFullyCompleted, selectedCard } = this.state;

    if (!isFullyCompleted && selectedCard) {
      return (
        <Box display='flex' flexDirection='column' alignItems='center'>
          <CreditCardPreview brand={selectedCard.brand} number={selectedCard.number} dueDate={selectedCard.dueDate} />
          <br />
          {selectedCard.isExpired && <Alert variant='error'>{'La carte est expirée.'}</Alert>}
          {selectedCard.isSoonExpired && <Alert variant='warning'>{'La carte arrive bientôt à expiration.'}</Alert>}
          {!selectedCard.isExpired ? (
            <React.Fragment>
              <Button color='secondary' variant='contained' disabled={buttonLoading} onClick={this.onCardConfirm}>
                {'Valider cette carte'}
              </Button>
              <br />
              <Button color='secondary' disabled={buttonLoading} onClick={() => this.setState({ selectedCard: null })}>
                {'Utiliser une autre carte'}
              </Button>
            </React.Fragment>
          ) : (
            <Button
              color='secondary'
              variant='contained'
              disabled={buttonLoading}
              onClick={() => this.setState({ selectedCard: null })}
            >
              {'Utiliser une autre carte'}
            </Button>
          )}
          <br />
          <Typography variant='caption'>
            {
              'En cas de changement, la nouvelle carte bancaire sera aussi utilisée sur vos autres plans de prélèvement.'
            }
          </Typography>
        </Box>
      );
    }

    if (isFullyCompleted && selectedCard) {
      return (
        <Box display='flex' flexDirection='column' alignItems='center'>
          {!selectedCard.isExpired && (
            <React.Fragment>
              <CheckCircleOutlineIcon fontSize='large' color='primary' />
              <br />
              <Typography gutterBottom color='primary'>
                {'Carte renseignée avec succès.'}
              </Typography>
            </React.Fragment>
          )}
          <CreditCardPreview brand={selectedCard.brand} number={selectedCard.number} dueDate={selectedCard.dueDate} />
          <br />
          {selectedCard.isExpired && <Alert variant='error'>{'La carte est expirée.'}</Alert>}
          {selectedCard.isSoonExpired && <Alert variant='warning'>{'La carte arrive bientôt à expiration.'}</Alert>}
          {(selectedCard.isExpired || selectedCard.isSoonExpired) && (
            <React.Fragment>
              <Button
                color='secondary'
                variant='contained'
                disabled={buttonLoading}
                onClick={() => this.setState({ selectedCard: null })}
              >
                {'Utiliser une autre carte'}
              </Button>
              <br />
              <Typography variant='caption'>
                {
                  'En cas de changement, la nouvelle carte bancaire sera aussi utilisée sur vos autres plans de prélèvement.'
                }
              </Typography>
            </React.Fragment>
          )}
          <br />
          <Typography>
            {
              'L\'ensemble des prélèvements effectués pour le compte de votre golf apparaîtront sous la mention "GOLF PRIMA" sur votre relevé bancaire.'
            }
          </Typography>
        </Box>
      );
    }

    // !isFullyCompleted && !selectedCard
    const { paylineToken } = this.state;

    return (
      <Paper style={{ padding: 20 }}>
        {paylineToken ? (
          <PaylineWidgetFrame
            token={paylineToken}
            options={{ flowType: PAYLINE_FLOW_TYPES.SAVE_CARD }}
            title='Payline Payment Widget'
          />
        ) : (
          <Button
            fullWidth
            color='secondary'
            variant='contained'
            disabled={buttonLoading}
            onClick={this.payWithNewCardPayline}
          >
            {'Ajouter une carte bancaire'}
          </Button>
        )}
        {currentCard && !selectedCard && (
          <Button fullWidth color='secondary' onClick={() => this.setState({ selectedCard: currentCard })}>
            {'Retour'}
          </Button>
        )}
      </Paper>
    );
  }

  // Verrou de ré-entrée SYNCHRONE pour la confirmation d'une carte existante.
  _cardConfirmInFlight = false;

  // On confirme une carte existante et valide
  onCardConfirm = () => {
    if (this._cardConfirmInFlight) return Promise.resolve();
    this._cardConfirmInFlight = true;
    const { account, membership, golf, selectedCard } = this.state;

    this.setState({
      error: null,
      buttonLoading: true,
      status: 'Nous confirmons votre plan de prélèvement...',
    });

    return Membership.api
      .setMembershipSchedule(apolloClient, {
        golfId: golf.id,
        creditCardId: selectedCard.id,
        year: membership.year,
        title: membership.year,
        amount: membership.total.amount,
        reference: membership.reference,
        paymentSchedule: membership.paymentSchedule.map(({ date, amount }) => ({ date, amount })),
        accountId: account.id,
        skipWS: true,
      })
      .then(() => this.setState({ isFullyCompleted: true }))
      .catch((err) =>
        this.setState({
          status: null,
          error: err.message,
        }),
      )
      .finally(() => {
        this._cardConfirmInFlight = false;
        this.setState({ buttonLoading: false });
      });
  };

  // Verrou de ré-entrée SYNCHRONE : bloque un 2e tap avant que le rendu ne masque le bouton
  // (disabled={buttonLoading} seul est asynchrone, effectif au prochain rendu).
  _cardRegisterInFlight = false;

  // Payline widget: obtenir un token et afficher le widget
  payWithNewCardPayline = () => {
    if (this._cardRegisterInFlight) return Promise.resolve();
    this._cardRegisterInFlight = true;
    const { account } = this.state;
    const timestamp = Number(new Date());
    const shortId = account.id.split('-')[0];
    const reference = `MEM-${shortId}-${timestamp}`;

    this.setState({
      error: null,
      paylineToken: null,
      buttonLoading: true,
    });

    const baseUrl = window.location.href.split('?')[0];
    const returnUrl = `${baseUrl}?payline_action=success`;
    const cancelUrl = `${baseUrl}?payline_action=cancel`;

    return Payline.api
      .getToken(apolloClient, {
        amount: this.authorizationAmount,
        reference,
        returnUrl,
        cancelUrl,
        callbackUrl: `${baseUrl}?payline_action=callback`,
        action: '100',
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        this.setState({ paylineToken: res.token, buttonLoading: false });
        this.paylineWidgetData = {
          token: res.token,
          accountId: account.id,
          reference,
        };
        this.isPaylinePaymentHandled = false;
      })
      .catch((err) => {
        this.setState({
          error: err.message || 'Une erreur est survenue lors du chargement du formulaire de paiement.',
          paylineToken: null,
          buttonLoading: false,
        });
      })
      .finally(() => {
        this._cardRegisterInFlight = false;
      });
  };

  // Payline widget callback: carte enregistrée avec succès (3DS validé par Payline)
  handlePaylineCardSuccess = (eventData = {}) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const data = this.paylineWidgetData;
    const token = eventData?.token || data.token;
    const uuid = helpers.uuid.createUuid();
    const { account, membership, golf, isFullyCompleted } = this.state;

    this.setState({
      error: null,
      buttonLoading: true,
      status: 'Nous vérifions votre carte...',
    });

    return Payline.api
      .captureWithToken(apolloClient, {
        amount: this.authorizationAmount,
        token,
        reference: data.reference,
        uuid,
        customer: {
          firstName: account.firstname,
          lastName: account.lastname,
          email: account.email,
        },
      })
      .then((result) => {
        if (!result) {
          throw new Error('Impossible de finaliser le paiement Payline.');
        }

        const cardBrand = result.cardBrand || result.brand;
        const dueDateRaw = result.expiryDate || result.expirationDate || '';
        const dueDate = dueDateRaw.replace(/\D/g, '') || '0000';
        const maskedCardNumber = (result.maskedCardNumber || '****').replace(/X/g, '*');
        // Voir ChangeCreditCard : pas de repli sur `uuid`, un wallet absent doit le rester.
        const walletId = result.walletId || null;

        const imprint = {
          id: uuid,
          token: result.token || result.transactionId || token,
          name: 'Payline web',
          number: maskedCardNumber,
          dueDate,
          brand: cardBrand,
          country: result.cardCountry,
          walletId,
          paymentRecordId: result.transactionId,
        };

        return CreditCard.api
          .setCreditCard(apolloClient, {
            id: imprint.id,
            token: imprint.token,
            accountId: account.id,
            number: imprint.number,
            walletId: imprint.walletId,
            paymentRecordId: imprint.paymentRecordId,
            ...helpers.object.filterByKeys(imprint, ['name', 'dueDate', 'type', 'brand', 'country']),
          })
          .then(() => imprint);
      })
      .then((imprint) => {
        // Si le plan de prélèvement de CETTE saison existait déjà, inutile d'en
        // recréer un : le rattachement de la nouvelle carte à l'échéancier est
        // assuré côté master à l'enregistrement de la carte (CreditCardService
        // .saveCreditCard -> changeCreditCard + repointUnusableSchedules).
        if (isFullyCompleted) {
          this.setState({
            selectedCard: imprint,
            paylineToken: null,
            buttonLoading: false,
            status: null,
          });
          return;
        }

        this.setState({ status: 'Nous confirmons votre plan de prélèvement...' });

        return Membership.api
          .setMembershipSchedule(apolloClient, {
            golfId: golf.id,
            creditCardId: imprint.id,
            year: membership.year,
            title: membership.year,
            amount: membership.total.amount,
            reference: membership.reference,
            paymentSchedule: membership.paymentSchedule.map(({ date, amount }) => ({ date, amount })),
            accountId: account.id,
            skipWS: true,
          })
          .then(() =>
            this.setState({
              selectedCard: imprint,
              isFullyCompleted: true,
              paylineToken: null,
              buttonLoading: false,
              status: null,
            }),
          );
      })
      .catch((err) => {
        this.isPaylinePaymentHandled = false;
        this.setState({
          status: null,
          error: err.message,
          paylineToken: null,
          buttonLoading: false,
        });
      });
  };

  handlePaylineCardError = (eventData = {}, defaultMessage = 'Le paiement a échoué. Veuillez réessayer.') => {
    this.setState({
      paylineToken: null,
      error: eventData?.message || defaultMessage,
      buttonLoading: false,
    });
    this.isPaylinePaymentHandled = false;
  };

  // Supprimé: onNewCardSubmit (remplacé par le flux Payline widget ci-dessus)

  renderHeader() {
    const { golf, account, membership } = this.state;

    const title = membership.members[0].category;

    return (
      <div>
        <Typography variant='h5' gutterBottom>
          {golf.name}
        </Typography>
        <Typography variant='subtitle1' gutterBottom>
          {'Prélèvement par carte bancaire'}
        </Typography>
        <br />
        <Typography variant='body2'>
          {'Bonjour '}
          <b>{`${account.firstname} ${account.lastname}`}</b>
          {','}
        </Typography>
        <Typography variant='body2'>
          {'Le réglement de votre cotisation '}
          <b>{title}</b>
          {' se fera par prélèvement sur votre carte bancaire.'}
        </Typography>
        <Typography variant='body2'>
          {'Pour ce faire, veuillez valider vos coordonnées bancaires ci-dessous.'}
        </Typography>
        <br />
      </div>
    );
  }

  /* */
  render() {
    if (this.state.isIframeReturn) {
      return null;
    }

    const {
      // ux
      error,
      status,
      buttonLoading,
      screenLoading,
      // data
      account,
      membership,
    } = this.state;

    return (
      <Screen noDrawer error={error} loading={screenLoading}>
        {account && membership && (
          <React.Fragment>
            {this.renderHeader()}
            <Grid container spacing={40} justify='center'>
              <Grid item md={6}>
                <Typography variant='h6'>{'Mon plan de paiement'}</Typography>
                <br />
                {this.renderSchedule()}
              </Grid>
              <Grid item md={6}>
                <Typography variant='h6'>{'Ma carte bancaire'}</Typography>
                <br />
                {this.renderForm()}
              </Grid>
            </Grid>
            <DialogPaymentIP open={buttonLoading} status={status} />
          </React.Fragment>
        )}
      </Screen>
    );
  }
}

export default ConfirmMembershipSchedule;
